import React from "react";
import { Building2, FileCheck2, Ruler, Boxes, ShieldCheck, BedDouble, Users, Briefcase, House as Home, TrendingUp, ArrowRight, Clock, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
interface TimelineFactor {
  id: string;
  step: string;
  title: string;
  duration: string;
  description: string;
  icon: React.ElementType;
}
interface LifeTrigger {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  icon: React.ElementType;
}
const timelineFactors: TimelineFactor[] = [{
  id: "permits",
  step: "01",
  title: "City of Brookhaven permitting",
  duration: "4–8 weeks",
  description: "Zoning review, tree canopy ordinances, DeKalb County health clearances, and municipal building permit approvals handled directly with Brookhaven plan reviewers.",
  icon: FileCheck2
}, {
  id: "design",
  step: "02",
  title: "Design finalization",
  duration: "3–6 weeks",
  description: "Architectural drawings, 3D spatial elevations, interior finish selections, and complete structural specification alignment before construction breaks ground.",
  icon: Ruler
}, {
  id: "foundation",
  step: "03",
  title: "Foundation & structural planning",
  duration: "2–4 weeks",
  description: "Site grading, soil compaction testing, tie-in engineering to your existing foundation, and crawl space or slab perimeter forming.",
  icon: Building2
}, {
  id: "materials",
  step: "04",
  title: "Material ordering & staging",
  duration: "3–5 weeks",
  description: "Securing architectural trusses, windows, engineered lumber, exterior masonry, and specialty siding prior to framing to avoid on-site delays.",
  icon: Boxes
}, {
  id: "inspections",
  step: "05",
  title: "Sequential municipal inspections",
  duration: "Throughout build",
  description: "Footing, rough-in mechanical, electrical, plumbing, framing, insulation, and final certificate of occupancy verification by Brookhaven inspectors.",
  icon: ShieldCheck
}];
const lifeTriggers: LifeTrigger[] = [{
  id: "bedroom",
  title: "Need another bedroom",
  subtitle: "Personal space without relocating",
  description: "Avoid giving up your favorite Brookhaven neighborhood or low interest rate just because your sleeping arrangements are capped.",
  icon: BedDouble
}, {
  id: "family",
  title: "Growing family demands",
  subtitle: "Expanding daily living areas",
  description: "When shared living rooms and breakfast nooks become cluttered, an open-concept rear extension restores daily flow and comfort.",
  icon: Users
}, {
  id: "office",
  title: "Dedicated executive home office",
  subtitle: "Quiet, productive workspace",
  description: "Move beyond makeshift desks in dining rooms with an insulated, quiet home office wing designed for focused work and video meetings.",
  icon: Briefcase
}, {
  id: "multigen",
  title: "Multigenerational living suite",
  subtitle: "In-law suite or ground-floor retreat",
  description: "Provide privacy and accessibility for aging parents or returning family members with an independent ground-floor suite and bathroom.",
  icon: Home
}, {
  id: "resale",
  title: "Preparing to maximize future equity",
  subtitle: "Strategic neighborhood square footage",
  description: "Brookhaven values favor larger square footage footprints; strategic primary suites or double-story additions yield strong appraisal returns.",
  icon: TrendingUp
}];
export default function AdditionTimelineAndTriggers() {
  return <section id="additiontimelineandtriggers" data-nav="light" className="bg-background text-foreground py-20 lg:py-28">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section 1: Timeline Factors */}
        <div className="mb-24 lg:mb-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            
            {/* Left Context & Photo */}
            <div className="lg:col-span-5 flex flex-col gap-6">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="border-primary/40 text-foreground px-3 py-1 font-sans text-xs">
                  <Clock className="w-3.5 h-3.5 text-primary mr-1" />
                  Phase Roadmap
                </Badge>
              </div>

              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground">
                Critical timeline factors in Brookhaven additions
              </h2>

              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
                Successful additions run on methodical pre-construction schedules. We balance local city ordinances, architectural engineering, and material supply queues so building days stay predictable.
              </p>

              {/* Realistic construction framing image */}
              <div className="relative mt-2 overflow-hidden rounded-xl border border-border shadow-md aspect-4/3 bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_construction.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_construction.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/home_addition_framing_construction.png-wvc-sizes" alt="Precision timber framing and foundation construction for an expansive two-story residential home addition in Brookhaven" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-linear-to-t from-secondary/80 via-transparent to-transparent flex items-end p-6">
                  <div className="text-secondary-foreground text-sm font-medium">
                    Active build site: Structural foundation & frame tie-in
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-muted/60 border border-border/80 flex items-start gap-3">
                <div className="p-2 rounded-md bg-primary/10 text-primary shrink-0 mt-0.5">
                  <FileCheck2 className="w-4 h-4" />
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  <span className="font-semibold text-foreground">Local experience matters:</span> We liaise directly with City of Brookhaven Community Development on tree preservation permits and setbacks.
                </div>
              </div>
            </div>

            {/* Right Factor Steps */}
            <div className="lg:col-span-7 flex flex-col gap-4">
              {timelineFactors.map((factor, index) => {
              const IconComponent = factor.icon;
              return <Card key={factor.id} data-index={index} className="border-border/80 bg-card hover:border-primary/50 transition-all duration-300 shadow-xs hover:shadow-md">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs font-semibold px-2 py-1 rounded bg-secondary text-secondary-foreground">
                            {factor.step}
                          </span>
                          <CardTitle className="text-lg sm:text-xl font-serif font-semibold text-foreground">
                            {factor.title}
                          </CardTitle>
                        </div>
                        <Badge variant="secondary" className="text-xs font-sans tracking-wide">
                          {factor.duration}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-start gap-3.5 pt-1">
                        <div className="p-2 rounded-lg bg-accent text-accent-foreground shrink-0 mt-0.5">
                          <IconComponent className="w-4 h-4" />
                        </div>
                        <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                          {factor.description}
                        </CardDescription>
                      </div>
                    </CardContent>
                  </Card>;
            })}
            </div>

          </div>
        </div>

        {/* Section 2: 'Is it time to add on?' Life-moment Triggers */}
        <div className="pt-10 border-t border-border">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <div className="flex items-center gap-2 mb-3">
              <Badge variant="outline" className="border-primary/40 text-foreground px-3 py-1 font-sans text-xs">
                <Sparkles className="w-3.5 h-3.5 text-primary mr-1" />
                Readiness Evaluation
              </Badge>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight text-foreground mb-4">
              Is it time to add on?
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
              Major milestones often outpace your original floor plan. Discover the key life triggers that signal an addition is the smartest decision for your household.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            
            {/* Triggers List */}
            <div className="lg:col-span-7 grid grid-cols-1 gap-4">
              {lifeTriggers.map((trigger, i) => {
              const TriggerIcon = trigger.icon;
              return <div key={trigger.id} data-index={i} className="p-5 sm:p-6 rounded-xl border border-border bg-card hover:border-primary/60 transition-colors duration-200 flex items-start gap-4 shadow-xs">
                    <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                      <TriggerIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between gap-2 flex-wrap mb-1">
                        <h3 className="font-serif text-lg font-semibold text-foreground">
                          {trigger.title}
                        </h3>
                      </div>
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-2">
                        {trigger.subtitle}
                      </p>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {trigger.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Visual Callout Card with Addition Photo */}
            <div className="lg:col-span-5 flex flex-col justify-between rounded-2xl border border-border bg-card overflow-hidden shadow-sm">
              <div className="relative aspect-4/3 w-full overflow-hidden bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/completed_luxury_home_addition_exterior.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/completed_luxury_home_addition_exterior.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/completed_luxury_home_addition_exterior.png-wvc-sizes" alt="Completed sunroom and second-story master suite addition seamlessly integrated into a historic Brookhaven residence" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute top-4 left-4">
                  <Badge variant="secondary" className="shadow-xs backdrop-blur-sm bg-secondary/90 text-secondary-foreground">
                    Seamless Integration
                  </Badge>
                </div>
              </div>

              <div className="p-6 sm:p-8 flex flex-col gap-4">
                <h3 className="font-serif text-2xl font-medium text-foreground">
                  Stay in the neighborhood you love
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Moving entails broker fees, moving stress, and losing your established Brookhaven schools and community ties. An intentional addition builds custom equity tailored precisely to your family&apos;s routine.
                </p>

                <div className="pt-4 border-t border-border flex flex-col sm:flex-row items-center gap-3">
                  <Button asChild size="lg" className="w-full sm:w-auto font-sans">
                    <Link to="/ContactRequestAnEstimatePage">
                      Request addition estimate
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                    <Link to="/OurProjectsPage">
                      View recent additions
                    </Link>
                  </Button>
                </div>
              </div>
            </div>

          </div>
        </div>

      </div>
    </section>;
}